/*
 * @Descripttion: 备忘提醒 - 系统日历同步服务（生成 iCalendar/.ics 内容）
 * @version:
 * @Author: geory
 * @Date: 2026-08-14 17:00:00
 * @LastEditors: geory
 * @LastEditTime: 2026-08-14 17:00:00
 */

import {
  nextOccurrence,
  remindRrule,
  parseByWeekDay
} from "./memoRemindService";

function pad(n) {
  return n < 10 ? "0" + n : "" + n;
}

//ics文本转义
function escapeText(text) {
  return String(text || "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

//utf8字节长度
function utf8ByteLen(str) {
  return unescape(encodeURIComponent(str)).length;
}

//ics行折叠：每行不超过75字节，续行以空格开头
function foldLine(line) {
  let out = [];
  let cur = "";
  for (const ch of String(line)) {
    const next = cur + ch;
    if (utf8ByteLen(next) > 75) {
      out.push(cur);
      cur = " " + ch;
    } else {
      cur = next;
    }
  }
  if (cur) {
    out.push(cur);
  }
  return out.join("\r\n");
}

//"yyyy-MM-dd HH:mm" 本地时间 -> Date
function parseLocalTime(str) {
  if (!str) {
    return new Date();
  }
  return new Date(String(str).replace(/-/g, "/"));
}

//Date -> ICS UTC时间 20260814T010000Z（仅用于DTSTAMP）
function toIcsUtc(date) {
  return (
    date.getUTCFullYear() +
    pad(date.getUTCMonth() + 1) +
    pad(date.getUTCDate()) +
    "T" +
    pad(date.getUTCHours()) +
    pad(date.getUTCMinutes()) +
    pad(date.getUTCSeconds()) +
    "Z"
  );
}

//Date -> ICS本地墙上时间（浮动时间，不带Z/TZID）：日历按原样展示，
//避免导入后被标成GMT/UTC导致显示时间偏移（如晚上8点显示成早上8点）
function toIcsFloating(date) {
  return (
    date.getFullYear() +
    pad(date.getMonth() + 1) +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    pad(date.getMinutes()) +
    pad(date.getSeconds())
  );
}

/**
 * 生成单条备忘的ics内容
 * @param {Object} params
 * @param {String|Number} params.id 备忘id
 * @param {String} params.title 备忘内容（日历事件标题）
 * @param {String|Date} params.startTime 提醒时间 "yyyy-MM-dd HH:mm" 或 Date
 * @param {String|Date} [params.endTime] 结束时间（默认开始时间+1小时）
 * @param {String} [params.description] 事件描述
 * @param {Number} [params.alarmMinutes] 日历提醒提前分钟数（默认0=到点提醒）
 * @param {String} [params.rrule] 重复规则，如 "FREQ=DAILY"、"FREQ=WEEKLY;BYDAY=MO,WE"
 */
export function buildMemoIcs({
  id,
  title,
  startTime,
  endTime,
  description,
  alarmMinutes,
  rrule,
}) {
  const start = startTime instanceof Date ? new Date(startTime.getTime()) : parseLocalTime(startTime);
  const end = endTime
    ? (endTime instanceof Date ? new Date(endTime.getTime()) : parseLocalTime(endTime))
    : new Date(start.getTime() + 60 * 60 * 1000);
  //默认到点提醒（提前0分钟）；大于0则提前对应分钟
  const alarm = alarmMinutes || 0;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//WMInput//MemoReminder//CN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    "UID:memo-" + (id || Date.now()) + "-wminput@wminput",
    "DTSTAMP:" + toIcsUtc(new Date()),
    "DTSTART:" + toIcsFloating(start),
    "DTEND:" + toIcsFloating(end),
    "SUMMARY:" + escapeText(title),
  ];
  if (rrule) {
    lines.push("RRULE:" + rrule);
  }
  if (description) {
    lines.push("DESCRIPTION:" + escapeText(description));
  }
  lines.push("BEGIN:VALARM");
  lines.push("TRIGGER:-PT" + alarm + "M");
  lines.push("ACTION:DISPLAY");
  lines.push("DESCRIPTION:" + escapeText(title));
  lines.push("END:VALARM");
  lines.push("END:VEVENT");
  lines.push("END:VCALENDAR");

  return lines.map(foldLine).join("\r\n") + "\r\n";
}

/**
 * 根据备忘数据生成ics内容（支持4种提醒类型，重复类型带RRULE）
 * 无有效提醒返回 null
 * UID 由「备忘id+提醒配置」组成：修改提醒时间后重新同步会产生新UID，
 * 日历导入时才会生效（同UID的重复导入会被系统日历忽略，导致修改时间不生效）
 */
export function buildMemoIcsFromItem(item) {
  if (!item || item.remindType == null || item.remindType == -1) {
    return null;
  }
  const occurrence = nextOccurrence(item);
  if (!occurrence) {
    return null;
  }
  const uidKey = remindFingerprint(item);
  return buildMemoIcs({
    id: uidKey,
    title: item.content,
    startTime: occurrence,
    description: "微脉输入法-备忘提醒",
    alarmMinutes: 0,
    rrule: remindRrule(item),
  });
}

//备忘id + 提醒配置 指纹（修改提醒后指纹变化 → 新UID）
function remindFingerprint(item) {
  const base = item.id != null ? item.id : (item.content || "memo");
  const type = Number(item.remindType);
  if (type === 0) {
    return base + "-t" + (item.notRepeatTime || 0);
  }
  if (type === 1) {
    return base + "-d" + (item.byDayTime || 0);
  }
  if (type === 2) {
    const days = parseByWeekDay(item.byWeekDay).slice().sort((a, b) => a - b);
    return base + "-w" + JSON.stringify(days) + "-" + (item.byDayTime || 0);
  }
  if (type === 3) {
    return base + "-m" + (item.byMonthDay || 0) + "-" + (item.byDayTime || 0);
  }
  return String(base);
}

/**
 * 浏览器模式：下载ics文件
 */
export function downloadMemoIcs(title, ics) {
  //UTF-8 BOM保证Windows下中文正常
  const blob = new Blob(["\uFEFF" + ics], {
    type: "text/calendar;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = (title || "备忘").slice(0, 30) + ".ics";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
